import React, { useMemo } from 'react';
import ButtonMultiDropdown from 'shared/components/andtComponents/ButtonMultiDropdown';
import { isEmptyArray } from 'shared/utils/arrayUtils';
import CustomCSVDownload from '../../../buttons/CustomCSVDownload';
import icons from '../icons';
import styles from '../ChartButtonsMenu.module.scss';
import { useCurrencyContext } from 'app/contexts/Currency/CurrencyContext';

const { OverwriteIcon, SaveReportIcon, SavePanelIcon, ExportCsvIcon, ExportPngIcon, GoalIcon } = icons.download;

const DownloadMenu = ({
  saveReportHandler,
  overwriteReportHandler,
  saveDashboardHandler,
  saveGoalHandler,
  overwriteDashboardHandler,
  exportToCSVHandler,
  exportToPNGHandler,
}) => {
  const currencyContext = useCurrencyContext();
  const saveAsGoalOption = useMemo(() => {
    const blockGoalsForNonProviderCurrency = currencyContext.currencyInUse?.code !== currencyContext.providerCurrency;
    if (blockGoalsForNonProviderCurrency) {
      if (saveGoalHandler?.options) {
        delete saveGoalHandler.options;
      }
      return {
        handler: saveGoalHandler,
        icon: GoalIcon,
        text: 'Save as Goal',
        id: 'aveAsGoal',
        disabled: true,
        tooltipText: `Currency adjustments are not supported for goals, change the currency back to the provider's current currency to see it`,
      };
    }
    return {
      handler: saveGoalHandler,
      icon: GoalIcon,
      text: 'Save as Goal',
      id: 'aveAsGoal',
      subMenuContent: (
        <>
          <li onClick={() => saveGoalHandler?.onClick()} automation-id="create-new">
            <span>
              <b>Create new</b>
            </span>
          </li>
          {saveGoalHandler &&
            saveGoalHandler?.options?.map((item) => (
              <li key={item.uuid} onClick={() => saveGoalHandler?.onClick(item.uuid)} id={item.uuid}>
                <span>{item.title}</span>
              </li>
            ))}
        </>
      ),
    };
  }, [currencyContext.currencyInUse, currencyContext.providerCurrency, saveGoalHandler]);

  const options = useMemo(
    () =>
      [
        {
          handler: saveReportHandler,
          icon: SaveReportIcon,
          text: 'Save as Report',
          id: 'saveAsReport',
        },
        saveAsGoalOption,
        {
          handler: overwriteReportHandler,
          icon: OverwriteIcon,
          text: 'Overwrite Existing Report',
          id: 'overwriteExistingReport',
        },
        {
          handler: exportToPNGHandler,
          icon: ExportPngIcon,
          text: 'Export as PNG',
          id: 'exportAsPNG',
        },
        {
          handler: exportToCSVHandler,
          id: 'exportAsCSV',
          simpleOptionContent: (
            <li className={styles.downloadCsvOptionContainer}>
              <CustomCSVDownload
                fetchData={exportToCSVHandler.onClick}
                filesNumber={1}
                isLoading={false}
                showDownloadIcon={false}
                hideText
                classNames={`btn-no-style ${styles.downloadCsvOption}`}
              >
                <span>
                  <ExportCsvIcon /> Export as CSV
                </span>
              </CustomCSVDownload>
            </li>
          ),
        },
        {
          handler: overwriteDashboardHandler,
          icon: OverwriteIcon,
          text: 'Overwrite Existing Dashboard',
          id: 'overwriteExistingDashboard',
        },
        {
          handler: saveDashboardHandler,
          icon: SavePanelIcon,
          text: 'Save as Dashboard Panel',
          id: 'saveAsDashboardPanel',
        },
      ]
        .filter((opt) => !!opt.handler)
        ?.map(
          (
            { handler, icon, text, content, simpleOptionContent, tooltipText, subMenuContent, id, disabled },
            index,
          ) => ({
            id: id || index,
            simpleOption: isEmptyArray(handler.options),
            label: content || text,
            content: subMenuContent,
            simpleOptionContent,
            icon,
            disabled: disabled ?? handler.disabled,
            onClick: handler.onClick,
            tooltipText,
          }),
        ),
    [
      saveReportHandler,
      saveAsGoalOption,
      overwriteReportHandler,
      exportToPNGHandler,
      exportToCSVHandler,
      overwriteDashboardHandler,
      saveDashboardHandler,
    ],
  );
  return (
    <ButtonMultiDropdown
      position="left"
      dropdownProps={{
        tooltipText: 'Save & Export Options',
        isSecondary: false,
        icon: icons.menu.downloadIcon,
      }}
      automationid="save"
      items={options}
    />
  );
};

export default DownloadMenu;
